import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { genericEntityService } from "../../../services/genericEntityService";
import { M_Button, M_Header, M_InputTextField, M_SwitchButton, M_Select } from "../../../M_Components";
import { PAGE_STATE } from "./AdminPage";
import './adminPage.css';
import { refresherSignal } from "../menu/refresher-signal";
import { useErrorHandler } from "../shared/errorHandler";
import { FormHelperText } from "@mui/material";

const MAX_ENTITY_CODE_LENGTH = 125;
const MAX_ENTITY_CAPTION_LENGTH = 255;
export const EntityEditForm = ({ setPageState, entity, entities }) => {
    const [t, i18n] = useTranslation();
    const [languages, setLanguages] = useState([]);
    const [entityForm, setEntityForm] = useState({
        entityId: null,
        code: null,
        captions: {},
        showInMenu: false,
        extendedEntity: null
    });

    const [errors, setErrors] = useState({
        code: null,
        captions: null
    });

    const { handleHttpError } = useErrorHandler();

    const [isSaveClicked, setIsSaveClicked] = useState(false);
    const [attemptedSave, setAttemptedSave] = useState(false);

    const [extendableEntities, setExtendableEntities] = useState([]);

    const isAlphanumeric = (input) => {
        const alphanumericRegex = /^[a-zA-Z0-9]+$/;
        return alphanumericRegex.test(input);
    }

    const canSave = () => {
        if (!entityForm.code || entityForm.code.trim().length === 0) {
            return false;
        }

        const hasCaptions = Object.values(entityForm.captions).some(caption => caption && caption.trim().length > 0);
        if (!hasCaptions) {
            return false;
        }

        for (let key in errors) {
            if (errors[key] !== null) {
                return false;
            }
        }
        return true;
    }

    const handleSaveEntity = () => {
        setAttemptedSave(true);

        if (!canSave()) {
            return;
        }

        setIsSaveClicked(true);
        if (entity === null) {
            genericEntityService.createEntity({
                ...entityForm,
                fields: []
            })
                .then(function (response) {
                    setPageState(PAGE_STATE.None, null);
                    refresherSignal.value = true;
                })
                .catch(function (error) {
                    handleHttpError(error);
                    setIsSaveClicked(false);
                });
        } else {
            const updatedEntity = {
                ...entity,
                ...entityForm
            }
            genericEntityService.updateEntityById(entity.EntityId, updatedEntity)
                .then(function (response) {
                    setPageState(PAGE_STATE.None, null);
                    refresherSignal.value = true;
                })
                .catch(function (error) {
                    handleHttpError(error);
                    setIsSaveClicked(false);
                });
        }
    }

    const handleCancel = () => setPageState(PAGE_STATE.None, null);

    useEffect(() => {
        genericEntityService.getLanguages()
            .then(function (response) {
                setLanguages([
                    ...response.data
                ])
            })
            .catch(function (error) {
                handleHttpError(error);
            });
        
        genericEntityService.getExtendableEntities()
            .then(function (response) {
                setExtendableEntities([
                    ...response.data
                ])
            })
            .catch(function (error) {
                handleHttpError(error);
            });
    }, [])

    useEffect(() => {
        if (entity !== null) {
            setEntityForm({
                entityId: entity.EntityId,
                code: entity.Code,
                captions: entity.Captions,
                showInMenu: entity.ShowInMenu,
                extendedEntity: entity.ExtendedEntity
            })
        } else {
            setEntityForm({
                entityId: null,
                code: null,
                captions: {},
                showInMenu: false,
                extendedEntity: null
            })
        }
    }, [entity])

    useEffect(() => {
        var errors = {
            code: null,
            captions: null
        };

        if (entityForm.code === null || entityForm.code === undefined || entityForm.code.trim().length === 0) {
            errors["code"] = t("NgeRequired");
        } else if (!isAlphanumeric(entityForm.code)) {
            errors["code"] = t("NgeInvalidChar");
        } else if (entityForm.code?.length > MAX_ENTITY_CODE_LENGTH) {
            errors["code"] = t("NgeInvalidLength");
        } else if (entity === null && entities.map(e => e.Code).includes(entityForm.code)) {
            errors["code"] = t("NgeDuplicateCode");
        } else if (entity !== null && entities.filter(e => e.EntityId !== entity.EntityId).map(e => e.Code).includes(entityForm.code)) {
            errors["code"] = t("NgeDuplicateCode");
        }

        const hasCaptions = Object.values(entityForm.captions).some(caption => caption && caption.trim().length > 0);
        if (!hasCaptions) {
            errors["captions"] = t("NgeRequired");
        }

        Object.keys(entityForm.captions).forEach(language => {
            if (entityForm.captions[language]?.length > 0 && entityForm.captions[language]?.length > MAX_ENTITY_CAPTION_LENGTH) {
                errors["captions"] = {
                    ...errors["captions"],
                    [language]: t("NgeInvalidLength")
                };
            }
        });

        setErrors((prev) => ({
            ...prev,
            ...errors
        }));

    }, [entityForm.code, entityForm.captions])

    const handleCodeChange = (value) => {
        setEntityForm((prev) => ({
            ...prev,
            code: value
        }))
    }
    const handleCaptionChange = (value, language) => {
        setEntityForm((prev) => ({
            ...prev,
            captions: {
                ...prev.captions,
                [language]: value
            }
        }))
    }
    const handleShowInMenuChange = (value) => {
        setEntityForm((prev) => ({
            ...prev,
            showInMenu: value
        }))
    }

    const handleExtendedEntity = (value) => {
           setEntityForm((prev) => ({
            ...prev,
            extendedEntity: value
        }))
    }


    return (
        <div className="entity-edit-grid">
            <div className="header entity-edit-header">
                <M_Header
                    color={'var(--color-white)'}
                    element={
                        <div className="entity-edit-header-content">
                            <div className="text-title ge-admin-subtitle">
                                {entity === null ? t("NgeCreateEntity") : t("NgeEditEntity")}
                            </div>
                            <div className="entity-edit-buttons">
                                <M_Button
                                    tooltip={t("NgeSave")}
                                    label={t("NgeSave")}
                                    icon={<i className='pi pi-check'></i>}
                                    onClick={handleSaveEntity}
                                    disabled={!canSave() || isSaveClicked}
                                />
                                <M_Button
                                    tooltip={t("NgeCancel")}
                                    label={t("NgeCancel")}
                                    icon={<i className='pi pi-times'></i>}
                                    onClick={handleCancel}
                                />
                            </div>
                        </div>
                    }
                />
            </div>
            <div className="entity-edit-form">
                <M_InputTextField
                    label={t("NgeCode")}
                    value={entityForm.code ?? ""}
                    required
                    helperTextMessage={attemptedSave ? errors['code'] : null}
                    color={attemptedSave && errors["code"] !== null ? 'var(--color-red)' : null}
                    onChange={(e) => handleCodeChange(e.target.value)}
                />
                <FormHelperText>{errors['code'] !== null && entityForm.code ? t(errors['code']) : null}</FormHelperText>
                <M_SwitchButton checked={entityForm.showInMenu ?? false} color={"var(--color-blue)"} label={<div className="text" >{t("NgeShowInMenu")}</div>} onClick={(e) => handleShowInMenuChange(e.target.checked)} />
                <M_Select
                    label={t("NgeExtendableEntity")}
                    value={entityForm.extendedEntity ?? ""}
                    options={extendableEntities.map(el => ({
                        label: el.DisplayName,
                        value: el.Id,
                    })
                    )}
                    disabled={entity !== null}
                    onChange={(e) => handleExtendedEntity(e.target.value)}
                />
                {
                    languages.map(l => {
                        const captionError = typeof errors.captions === 'string' ? errors.captions :
                            (errors.captions !== null && errors['captions'][l.LanguageCode] !== undefined) ? errors['captions'][l.LanguageCode] : null;

                        return (
                            <M_InputTextField
                                key={l.LanguageCode}
                                label={`${t("NgeCaption")} ${t(l.LanguageCode)}`}
                                helperTextMessage={attemptedSave ? captionError : null}
                                color={attemptedSave && captionError !== null ? 'var(--color-red)' : null}
                                value={entityForm.captions[l.LanguageCode] ?? ""}
                                onChange={(e) => handleCaptionChange(e.target.value, l.LanguageCode)}
                                required={true}
                            />
                        )
                    })
                }
            </div>
        </div>
    )
}

